/**
 * The two markers every Elevate governance screen must show, in one place.
 *
 * TEST-ONLY (GOV-09) is the reason this component exists at all. The database
 * keeps test-only material away from a real tenant's trainees (migrations 251
 * and 263), but the person approving and publishing it has to SEE the marking
 * before they act — the enforcement is invisible to them by design, and an
 * unbadged row is how test material reaches a production wave.
 *
 * AI-AUTHORED (GOV-07) is the second: AI-generated content stays in Draft until
 * a human approval exists, and the reviewer needs to know which rows those are.
 */
import React from 'react';
import { AlertTriangle, Bot, FlaskConical } from 'lucide-react';
import { LIFECYCLE_LABELS, type ContentLifecycleStatus } from '../../../lib/elevateGovernance';

export const TestOnlyBadge: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <span
    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300"
    title="GOV-09: test-only content is publishable to test tenants only and is excluded from any production release"
  >
    <FlaskConical className="w-3 h-3" />
    {compact ? 'TEST' : 'TEST-ONLY'}
  </span>
);

export const AiAuthoredBadge: React.FC = () => (
  <span
    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-100 text-violet-900 border border-violet-300"
    title="GOV-07: AI-generated content remains in Draft until human review and approval are complete"
  >
    <Bot className="w-3 h-3" />
    AI-authored
  </span>
);

const STATUS_STYLES: Record<ContentLifecycleStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  technical_review: 'bg-sky-50 text-sky-800 border-sky-200',
  educational_review: 'bg-sky-50 text-sky-800 border-sky-200',
  awaiting_approval: 'bg-blue-50 text-blue-800 border-blue-200',
  approved: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  published: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  under_review: 'bg-orange-50 text-orange-800 border-orange-200',
  superseded: 'bg-gray-100 text-gray-600 border-gray-300',
  archived: 'bg-gray-100 text-gray-500 border-gray-300',
};

export const LifecycleBadge: React.FC<{ status: ContentLifecycleStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
      STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-700 border-gray-300'
    }`}
  >
    {LIFECYCLE_LABELS[status] ?? status}
  </span>
);

/**
 * A refusal, shown with the database's own words. See the note in
 * lib/elevateGovernance.ts: the rule's wording is the evidence.
 */
export const GuardRefusal: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    className="flex items-start gap-2 px-3 py-2 rounded border border-red-300 bg-red-50 text-sm text-red-900"
  >
    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <span className="font-mono text-xs break-all">{message}</span>
  </div>
);
